/* global globalThis */
import React, { useState, useEffect, useRef } from 'react';
import './dashboard.css';
import ClearFilter from '../../components/ClearFilter';
import ExportTable from '../../components/ExportTable';
import Pagination from '../../components/Pagination';
import { apiFetch } from '../../utils/api';
const safeGlobal = typeof globalThis !== 'undefined' ? globalThis : window;

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [courseStats, setCourseStats] = useState([]);
  const [userCertificates, setUserCertificates] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [usersData, setUsersData] = useState([]);

  const [showNonNullCertificates, setShowNonNullCertificates] = useState(false);

  const [courseFilters, setCourseFilters] = useState({
    courseName: '',
    department: '',
    totalUsers: '',
    videoCount: '',
    certificatesIssued: '',
  });
  const [certificateFilters, setCertificateFilters] = useState({
    userName: '',
    email: '',
    courseName: '',
    certificateId: '',
    issuedAt: '',
  });
  const [feedbackFilters, setFeedbackFilters] = useState({
    userId: '',
    email: '',
    category: '',
    submittedAt: '',
  });
  const [departmentFilters, setDepartmentFilters] = useState({
    name: '',
  });
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const [coursePage, setCoursePage] = useState(1);
  const [certificatePage, setCertificatePage] = useState(1);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [departmentPage, setDepartmentPage] = useState(1);
  const [userPage, setUserPage] = useState(1);

  const [courseItemsPerPage, setCourseItemsPerPage] = useState(10);
  const [certificateItemsPerPage, setCertificateItemsPerPage] = useState(10);
  const [feedbackItemsPerPage, setFeedbackItemsPerPage] = useState(10);
  const [departmentItemsPerPage, setDepartmentItemsPerPage] = useState(10);
  const [userItemsPerPage, setUserItemsPerPage] = useState(10);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [isCertificateFilter, setIsCertificateFilter] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [showUserStatsModal, setShowUserStatsModal] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const userManagementRef = useRef(null);
  const departmentTableRef = useRef(null);
  const usersTableRef = useRef(null);
  const courseStatsRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const summaryResponse = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/summary`);
        setDashboardData(summaryResponse);

        const statsResponse = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/course-stats`);
        setCourseStats(statsResponse || []);

        const certResponse = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/user-certificates`);
        setUserCertificates(certResponse || []);

        const feedbackResponse = await apiFetch(`${window._env_.API_BASE_URL}/feedback/feedbackstats`);
        setFeedbackData(feedbackResponse || []);

        const deptsResponse = await apiFetch(`${window._env_.API_BASE_URL}/departments/summary`);
        setDepartmentsData(deptsResponse || []);

        const usersStatsResponse = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/user-stats`);
        setUsersData(usersStatsResponse || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N.A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N.A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleCourseFilterChange = (e) => {
    const { name, value } = e.target;
    setCourseFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCoursePage(1);
  };

  const handleCertificateFilterChange = (e) => {
    const { name, value } = e.target;
    setCertificateFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCertificatePage(1);
  };

  const handleFeedbackFilterChange = (e) => {
    const { name, value } = e.target;
    setFeedbackFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFeedbackPage(1);
  };

  const handleDepartmentFilterChange = (e) => {
    const { name, value } = e.target;
    setDepartmentFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setDepartmentPage(1);
  };



  const handleViewCertificate = (certificateId) => {
    if (certificateId) {
      const url = `${window._env_.API_BASE_URL}/certificates/view/${certificateId}.pdf`;
      setCertificateUrl(url);
      setShowCertificateModal(true);
    }
  };

  const closeCertificateModal = () => {
    setShowCertificateModal(false);
    setCertificateUrl('');
  };

  const handleUserStatsClick = async (userId, userName, email) => {
    setLoadingStats(true);
    setSelectedUser({ userName, email });
    try {
      const response = await apiFetch(`${window._env_.API_BASE_URL}/users/${userId}/insights`);
      setUserStats(response);
      setShowUserStatsModal(true);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const closeUserStatsModal = () => {
    setShowUserStatsModal(false);
    setUserStats(null);
    setSelectedUser(null);
  };

  const [expandedRows, setExpandedRows] = useState({});

  const matchesFilter = (text, filterValue) => {
    return text.toLowerCase().includes(filterValue.toLowerCase());
  };

  const matchesNumberFilter = (value, filterValue) => {
    return filterValue === '' || value.toString().includes(filterValue);
  };

  const filteredCourseStats = courseStats.filter((stat) => {
    return (
      matchesFilter(stat.course_name, courseFilters.courseName) &&
      matchesFilter(stat.department_name, courseFilters.department) &&
      matchesNumberFilter(stat.video_count, courseFilters.videoCount) &&
      matchesNumberFilter(stat.certificates_issued, courseFilters.certificatesIssued)
    );
  });

  const filteredCertificates = userCertificates.filter((cert) => {
    const passesNullCheck = !showNonNullCertificates || cert.certificate_id;
    return (
      passesNullCheck &&
      matchesFilter(cert.user_name, certificateFilters.userName) &&
      matchesFilter(cert.email, certificateFilters.email) &&
      matchesFilter(cert.course_name, certificateFilters.courseName) &&
      matchesNumberFilter(cert.certificate_id || '', certificateFilters.certificateId) &&
      matchesNumberFilter(cert.certificate_issued_at || '', certificateFilters.issuedAt)
    );
  });

  const filteredFeedback = feedbackData.filter((feedback) => {
    return (
      matchesFilter(feedback.user_id.toString(), feedbackFilters.userId) &&
      matchesFilter(feedback.useremail || '', feedbackFilters.email) &&
      matchesFilter(feedback.category || '', feedbackFilters.category) &&
      matchesFilter(feedback.submitted_at || '', feedbackFilters.submittedAt)
    );
  });

  const filteredDepartments = departmentsData.filter((dept) => {
    return matchesFilter(dept.department_name, departmentFilters.name);
  });

  const filteredUsersList = usersData.filter((user) => {
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      (user.username || '').toLowerCase().includes(query) ||
      (user.email || '').toLowerCase().includes(query) ||
      (user.department_name || '').toLowerCase().includes(query) ||
      (user.designation || '').toLowerCase().includes(query) ||
      (user.reporting_manager || '').toLowerCase().includes(query) ||
      (user.courses || '').toLowerCase().includes(query)
    );
  });

  const handleCertificatesCardClick = () => {
    setShowNonNullCertificates((prev) => !prev);
  };

  const resetCertificateFilters = () => {
    setSelectedCourseId(null);
    setIsCertificateFilter(false);
    setCertificateFilters({
      userName: '',
      email: '',
      courseName: '',
      certificateId: '',
      issuedAt: '',
    });
    setCertificatePage(1);
  };

  const scrollToUserManagement = () => {
    userManagementRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToDepartments = () => {
    departmentTableRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToUsers = () => {
    usersTableRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToCourseStats = () => {
    courseStatsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const setFilteredCertificatesForCourse = (courseId, courseName, shouldFilter = false) => {
    setSelectedCourseId(courseId);
    setIsCertificateFilter(shouldFilter);
    setCertificateFilters((prev) => ({
      ...prev,
      courseName: courseName,
    }));
    setCertificatePage(1);
  };

  const isCurrentFilter = (courseId, filterType) => {
    return courseId === selectedCourseId && isCertificateFilter === filterType;
  };

  const handleTotalUsersClick = async (courseId, courseName) => {
    try {
      if (isCurrentFilter(courseId, false)) {
        const response = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/user-certificates`);
        setUserCertificates(response);
        resetCertificateFilters();
      } else {
        const response = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/user-certificates`, {
          params: { course_id: courseId },
        });
        setUserCertificates(response);
        setFilteredCertificatesForCourse(courseId, courseName, false);
      }
      scrollToUserManagement();
    } catch (error) {
      console.error('Error fetching users by course:', error);
    }
  };

  const handleCertificatesIssuedClick = async (courseId, courseName, certificatesIssued) => {
    if (certificatesIssued === 0) return;

    try {
      if (isCurrentFilter(courseId, true)) {
        const response = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/user-certificates`);
        setUserCertificates(response);
        resetCertificateFilters();
      } else {
        const response = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/${courseId}/certificates`);
        setUserCertificates(response);
        setFilteredCertificatesForCourse(courseId, courseName, true);
      }
      scrollToUserManagement();
    } catch (error) {
      console.error('Error fetching certificates by course:', error);
    }
  };

  const clearCertificateFilter = async () => {
    try {
      const response = await apiFetch(`${window._env_.API_BASE_URL}/dashboard/user-certificates`);
      setUserCertificates(response);
      setSelectedCourseId(null);
      setIsCertificateFilter(false);
      setCertificateFilters({
        userName: '',
        email: '',
        courseName: '',
        certificateId: '',
        issuedAt: '',
      });
      setCertificatePage(1);
    } catch (error) {
      console.error('Error fetching unfiltered certificates:', error);
    }
  };

  const clearCourseStatsFilter = () => {
    setCourseFilters({
      courseName: '',
      department: '',
      totalUsers: '',
      videoCount: '',
      certificatesIssued: '',
    });
    setCoursePage(1);
  };

  const clearFeedbackFilter = () => {
    setFeedbackFilters({
      userId: '',
      email: '',
      category: '',
      submittedAt: '',
    });
    setFeedbackPage(1);
  };

  const clearDepartmentFilter = () => {
    setDepartmentFilters({ name: '' });
    setDepartmentPage(1);
  };

  const clearUserFilter = () => {
    setUserSearchQuery('');
    setUserPage(1);
  };

  const toggleExpandRow = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const renderLoadingRow = (colSpan) => (
    <tr>
      <td colSpan={colSpan} style={{ textAlign: 'center' }}>Loading...</td>
    </tr>
  );

  const renderEmptyRow = (colSpan) => (
    <tr>
      <td colSpan={colSpan} style={{ textAlign: 'center', color: 'red' }}>No records found</td>
    </tr>
  );

  const renderTableBody = (isLoading, data, colSpan, renderRows) => {
    if (isLoading) return renderLoadingRow(colSpan);
    if (data.length === 0) return renderEmptyRow(colSpan);
    return renderRows();
  };

  const renderCertificateRow = (cert, index) => (
    <tr key={index}>
      <td>
        <button
          type="button"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#007bff',
            textDecoration: 'underline'
          }}
          onClick={() => handleUserStatsClick(cert.user_id, cert.user_name, cert.email)}
        >
          {cert.user_name}
        </button>
      </td>
      <td>
        <button
          type="button"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#007bff',
            textDecoration: 'underline'
          }}
          onClick={() => handleUserStatsClick(cert.user_id, cert.user_name, cert.email)}
        >
          {cert.email}
        </button>
      </td>
      <td>{cert.course_name}</td>
      <td>
        {cert.certificate_id ? (
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#007bff',
              textDecoration: 'underline'
            }}
            onClick={() => handleViewCertificate(cert.certificate_id)}
          >
            {cert.certificate_id}
          </button>

        ) : (
          'N.A'
        )}
      </td>
      <td>{formatDate(cert.certificate_issued_at || cert.issued_at || null)}</td>
    </tr>
  );

  const getCertificateRowStyle = (certificatesIssued) => ({
    cursor: certificatesIssued === 0 ? 'default' : 'pointer',
    color: certificatesIssued === 0 ? '#6c757d' : '#007bff',
    textDecoration: certificatesIssued === 0 ? 'none' : 'underline',
  });

  const renderCourseRow = (stat, index) => (
    <tr key={index}>
      <td>{stat.course_name}</td>
      <td>{stat.department_name}</td>
      <td>
        <button
          type="button"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#007bff',
            textDecoration: 'underline'
          }}
          onClick={() => handleTotalUsersClick(stat.course_id, stat.course_name)}
        >
          {stat.total_users}
        </button>

      </td>
      <td>{stat.video_count}</td>
      <td>
        <button
          type="button"
          style={{
            ...getCertificateRowStyle(stat.certificates_issued),
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer'
          }}
          onClick={() =>
            handleCertificatesIssuedClick(
              stat.course_id,
              stat.course_name,
              stat.certificates_issued
            )
          }
        >
          {stat.certificates_issued}
        </button>
      </td>
    </tr>
  );

  const renderDepartmentRow = (dept, index) => (
    <tr key={index}>
      <td>{dept.department_name}</td>
      <td>{dept.description || 'N.A'}</td>
      <td>{dept.course_count}</td>
      <td>{dept.enroller_count}</td>
    </tr>
  );

  const renderDashboardUserRow = (user, index) => (
    <tr key={index}>
      <td>{user.username}</td>
      <td>{user.email}</td>
      <td>{user.designation || 'N.A'}</td>
      <td>{user.department_name || 'N.A'}</td>
      <td>{user.reporting_manager || 'N.A'}</td>
      <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.courses || 'None'}>
        {user.courses || 'None'}
      </td>
    </tr>
  );

  const renderTableRows = (items, page, itemsPerPage, renderRow) => {
    return items
      .slice((page - 1) * itemsPerPage, page * itemsPerPage)
      .map((item, index) => renderRow(item, index));
  };

  const renderFeedbackRow = (feedback, index) => {
    const rowKey = `${feedback.user_id}-${index}`;
    const text = feedback.feedback_text || 'N.A';
    const isLong = text.length > 120;
    const expanded = expandedRows[rowKey] || false;
    const preview = isLong ? text.substring(0, 120) + '...' : text;

    return (
      <tr key={rowKey}>
        <td>{feedback.user_id}</td>
        <td>{feedback.useremail || 'N.A'}</td>
        <td className="feedback-text">
          <div className={`fb-text ${expanded ? 'expanded' : ''}`}>
            {expanded ? text : preview}
          </div>
          {isLong && (
            <button className="show-more-btn" onClick={() => toggleExpandRow(rowKey)}>
              {expanded ? 'Show Less' : 'Show More'}
            </button>
          )}
        </td>
        <td>{feedback.category || 'N.A'}</td>
        <td>{formatDate(feedback.submitted_at)}</td>
      </tr>
    );
  };

  return (
    <div className="ad-dashboard-container">
      {dashboardData ? (
        <>
          {loadingStats && (
            <div className="ad-loading-overlay">
              <span className="ad-loading-spinner"></span>
            </div>
          )}
          <div className={`ad-content-wrapper ${loadingStats ? 'ad-blurred' : ''}`}>
            <div className="ad-cards-container">
              <div className="ad-card" onClick={scrollToUserManagement} style={{ cursor: 'pointer' }}>
                <h3>Certificates</h3>
                <p>{dashboardData.total_certificates}</p>
              </div>
              <div className="ad-card" onClick={scrollToDepartments} style={{ cursor: 'pointer' }}>
                <h3>Departments</h3>
                <p>{dashboardData.total_departments}</p>
              </div>
              <div className="ad-card" onClick={scrollToCourseStats} style={{ cursor: 'pointer' }}>
                <h3>Active Courses</h3>
                <p>{dashboardData.total_active_courses}</p>
              </div>
              <div className="ad-card" onClick={scrollToUsers} style={{ cursor: 'pointer' }}>
                <h3>Users</h3>
                <p>{dashboardData.total_users}</p>
              </div>
              <div className="ad-card">
                <h3>Enrolled Courses</h3>
                <p>{userCertificates.length}</p>
              </div>
            </div>
            <div className="ad-table-container" ref={userManagementRef}>
              <h2>Certificates</h2>
              <div className="ad-filter-container">
                <input
                  type="text"
                  name="userName"
                  placeholder="User Name"
                  value={certificateFilters.userName}
                  onChange={handleCertificateFilterChange}
                />
                <input
                  type="text"
                  name="email"
                  placeholder="Email"
                  value={certificateFilters.email}
                  onChange={handleCertificateFilterChange}
                />
                <input
                  type="text"
                  name="courseName"
                  placeholder="Course Name"
                  value={certificateFilters.courseName}
                  onChange={handleCertificateFilterChange}
                />
                <div className="ad-date-input-container">
                  <input
                    type="date"
                    name="issuedAt"
                    placeholder="Issued On"
                    value={certificateFilters.issuedAt}
                    onChange={handleCertificateFilterChange}
                  />
                  <span className="ad-calendar-icon">📅</span>
                </div>
                <ClearFilter onClear={clearCertificateFilter} />
                <ExportTable
                  data={filteredCertificates}
                  headers={['USER NAME', 'EMAIL', 'COURSE NAME', 'CERTIFICATE ID', 'ISSUED ON']}
                  tableName="Certificates"
                  className="ad-dashboard-export-btn"
                />
              </div>
              <div className="ad-table-scroll">
                <table className="ad-course-table">
                  <thead>
                    <tr>
                      <th>USER NAME</th>
                      <th>EMAIL</th>
                      <th>COURSE NAME</th>
                      <th>CERTIFICATE ID</th>
                      <th>ISSUED ON</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableBody(
                      isLoading,
                      filteredCertificates,
                      5,
                      () => renderTableRows(filteredCertificates, certificatePage, certificateItemsPerPage, renderCertificateRow)
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                items={filteredCertificates}
                itemsPerPage={certificateItemsPerPage}
                setItemsPerPage={setCertificateItemsPerPage}
                currentPage={certificatePage}
                setCurrentPage={setCertificatePage}
              />
            </div>
            {showCertificateModal && (
              <>
                <div className="ad-certificate-modal-overlay"></div>
                <div className="ad-certificate-modal" role="dialog" aria-modal="true">
                  <div className="ad-certificate-modal-header">
                    <h2>Certificate</h2>
                    <button
                      onClick={closeCertificateModal}
                      className="ad-certificate-modal-close"
                      aria-label="Close certificate popup"
                    >
                      &times;
                    </button>
                  </div>
                  <div className="ad-certificate-modal-content">
                    <iframe
                      src={certificateUrl}
                      title="Certificate"
                      frameBorder="0"
                      className="ad-certificate-iframe"
                    ></iframe>
                  </div>
                </div>
              </>
            )}
            {showUserStatsModal && (
              <>
                <div className="ad-user-stats-modal-overlay"></div>
                <div className="ad-user-stats-modal" role="dialog" aria-modal="true">
                  <div className="ad-user-stats-modal-header">
                    <h2>User Statistics</h2>
                    <button
                      onClick={closeUserStatsModal}
                      className="ad-user-stats-modal-close"
                      aria-label="Close user stats popup"
                    >
                      &times;
                    </button>
                  </div>
                  <div className="ad-user-stats-modal-content">
                    {userStats && selectedUser ? (
                      <div>
                        <table className="ad-user-stats-table">
                          <tbody>
                            <tr>
                              <td><strong>Name:</strong></td>
                              <td>{selectedUser.userName}</td>
                            </tr>
                            <tr>
                              <td><strong>Email:</strong></td>
                              <td>{selectedUser.email}</td>
                            </tr>
                            <tr>
                              <td><strong>Enrolled Courses:</strong></td>
                              <td>{userStats.enrolled_courses}</td>
                            </tr>
                            <tr>
                              <td><strong>Completed Courses:</strong></td>
                              <td>{userStats.completed_courses}</td>
                            </tr>
                            <tr>
                              <td><strong>In-Progress Courses:</strong></td>
                              <td>{userStats.in_progress_courses}</td>
                            </tr>
                            <tr>
                              <td><strong>Certificates Earned:</strong></td>
                              <td>{userStats.certificates_earned}</td>
                            </tr>
                            <tr>
                              <td><strong>Total Time Spent:</strong></td>
                              <td>{userStats.total_time_spent}</td>
                            </tr>
                            <tr>
                              <td><strong>Completion Rate:</strong></td>
                              <td>{userStats.completion_rate}%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>Loading user stats...</p>
                    )}
                  </div>
                </div>
              </>
            )}
            <div className="ad-table-container" ref={courseStatsRef}>
              <h2>Course Statistics</h2>
              <div className="ad-filter-container">
                <input
                  type="text"
                  name="courseName"
                  placeholder="Course Name"
                  value={courseFilters.courseName}
                  onChange={handleCourseFilterChange}
                />
                <input
                  type="text"
                  name="department"
                  placeholder="Department"
                  value={courseFilters.department}
                  onChange={handleCourseFilterChange}
                />
                <ClearFilter onClear={clearCourseStatsFilter} />
                <ExportTable
                  data={filteredCourseStats}
                  headers={['COURSE NAME', 'DEPARTMENT', 'TOTAL USERS', 'NO OF VIDEOS', 'NO OF CERTIFICATES ISSUED']}
                  tableName="Course_Statistics"
                  className="ad-dashboard-export-btn"
                />
              </div>
              <div className="ad-table-scroll">
                <table className="ad-course-table">
                  <thead>
                    <tr>
                      <th>COURSE NAME</th>
                      <th>DEPARTMENT</th>
                      <th>TOTAL USERS</th>
                      <th>NO OF VIDEOS</th>
                      <th>NO OF CERTIFICATES ISSUED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableBody(
                      isLoading,
                      filteredCourseStats,
                      5,
                      () => renderTableRows(filteredCourseStats, coursePage, courseItemsPerPage, renderCourseRow)
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                items={filteredCourseStats}
                itemsPerPage={courseItemsPerPage}
                setItemsPerPage={setCourseItemsPerPage}
                currentPage={coursePage}
                setCurrentPage={setCoursePage}
              />
            </div>
            <div className="ad-table-container" ref={departmentTableRef}>
              <h2>Departments</h2>
              <div className="ad-filter-container">
                <input
                  type="text"
                  name="name"
                  placeholder="Department Name"
                  value={departmentFilters.name}
                  onChange={handleDepartmentFilterChange}
                />
                <ClearFilter onClear={clearDepartmentFilter} />
                <ExportTable
                  data={filteredDepartments}
                  headers={['DEPARTMENT NAME', 'DESCRIPTION', 'COURSE COUNT', 'ENROLLED USERS COUNT']}
                  tableName="Departments"
                  className="ad-dashboard-export-btn"
                />
              </div>
              <div className="ad-table-scroll">
                <table className="ad-course-table">
                  <thead>
                    <tr>
                      <th>DEPARTMENT NAME</th>
                      <th>DESCRIPTION</th>
                      <th>COURSE COUNT</th>
                      <th>ENROLLED USERS COUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableBody(
                      isLoading,
                      filteredDepartments,
                      4,
                      () => renderTableRows(filteredDepartments, departmentPage, departmentItemsPerPage, renderDepartmentRow)
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                items={filteredDepartments}
                itemsPerPage={departmentItemsPerPage}
                setItemsPerPage={setDepartmentItemsPerPage}
                currentPage={departmentPage}
                setCurrentPage={setDepartmentPage}
              />
            </div>
            <div className="ad-table-container" ref={usersTableRef}>
              <h2>Users</h2>
              <div className="ad-filter-container">
                <input
                  type="text"
                  placeholder="Search Users (Name, Email, Dept, Role, Manager, Course)..."
                  value={userSearchQuery}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                    setUserPage(1);
                  }}
                  style={{ width: '320px' }}
                />
                <ClearFilter onClear={clearUserFilter} />
                <ExportTable
                  data={filteredUsersList}
                  headers={['Username', 'Email', 'Designation', 'Department', 'Reporting Manager', 'Enrolled Courses']}
                  tableName="Dashboard_Users"
                  className="ad-dashboard-export-btn"
                />
              </div>
              <div className="ad-table-scroll">
                <table className="ad-course-table">
                  <thead>
                    <tr>
                      <th>USERNAME</th>
                      <th>EMAIL</th>
                      <th>DESIGNATION</th>
                      <th>DEPARTMENT</th>
                      <th>REPORTING MANAGER</th>
                      <th>ENROLLED COURSES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableBody(
                      isLoading,
                      filteredUsersList,
                      6,
                      () => renderTableRows(filteredUsersList, userPage, userItemsPerPage, renderDashboardUserRow)
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                items={filteredUsersList}
                itemsPerPage={userItemsPerPage}
                setItemsPerPage={setUserItemsPerPage}
                currentPage={userPage}
                setCurrentPage={setUserPage}
              />
            </div>
            <div className="ad-table-container">
              <h2>Feedback Overview</h2>
              <div className="ad-filter-container">
                <input
                  type="text"
                  name="userId"
                  placeholder="User ID"
                  value={feedbackFilters.userId}
                  onChange={handleFeedbackFilterChange}
                />
                <input
                  type="text"
                  name="email"
                  placeholder="User Email"
                  value={feedbackFilters.email}
                  onChange={handleFeedbackFilterChange}
                />
                <select
                  name="category"
                  value={feedbackFilters.category}
                  onChange={handleFeedbackFilterChange}
                >
                  <option value="">All Categories</option>
                  <option value="Course">Course</option>
                  <option value="Platform">Platform</option>
                  <option value="Support">Support</option>
                  <option value="Others">Others</option>
                </select>
                <div className="ad-date-input-container">
                  <input
                    type="date"
                    name="submittedAt"
                    value={feedbackFilters.submittedAt}
                    onChange={handleFeedbackFilterChange}
                  />
                  <span className="ad-calendar-icon">📅</span>
                </div>
                <ClearFilter onClear={clearFeedbackFilter} />
                <ExportTable
                  data={filteredFeedback}
                  headers={['USER ID', 'USER EMAIL', 'FEEDBACK TEXT', 'CATEGORY', 'SUBMITTED AT']}
                  tableName="Feedback_Overview"
                  className="ad-dashboard-export-btn"
                />
              </div>
              <div className="ad-table-scroll">
                <table className="ad-course-table">
                  <thead>
                    <tr>
                      <th>USER ID</th>
                      <th>USER EMAIL</th>
                      <th>FEEDBACK TEXT</th>
                      <th>CATEGORY</th>
                      <th>SUBMITTED AT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableBody(
                      isLoading,
                      filteredFeedback,
                      5,
                      () => renderTableRows(filteredFeedback, feedbackPage, feedbackItemsPerPage, renderFeedbackRow)
                    )}
                  </tbody>

                </table>
              </div>
              <Pagination
                items={filteredFeedback}
                itemsPerPage={feedbackItemsPerPage}
                setItemsPerPage={setFeedbackItemsPerPage}
                currentPage={feedbackPage}
                setCurrentPage={setFeedbackPage}
              />
            </div>
          </div>
        </>
      ) : (
        <p>Loading data...</p>
      )}
    </div>
  );
};

export default Dashboard;